import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import '../../css/Pages.css';
import ScientificArticlesMngtListing from '../../components/ScientificManagement/ScientificArticlesMngt/ScientificArticlesMngtListing';
import ScientificArticleMngtForm from '../../components/ScientificManagement/ScientificArticlesMngt/ScientificArticlesMngtForm';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const PublicQ1Q2Article = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const yearParam = queryParams.get('year');

    const initialFromDate = yearParam ? new Date(parseInt(yearParam), 0, 1) : null;
    const initialToDate = yearParam ? new Date(parseInt(yearParam), 11, 31) : null;

    const toast = useRef(null);
    const [data, setData] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [modalMode, setModalMode] = useState(null);
    const [formData, setFormData] = useState({});
    const [members, setMembers] = useState([]);

    const [selectedAuthor, setSelectedAuthor] = useState(null);
    const [filteredAuthors, setFilteredAuthors] = useState([]);

    const [filters, setFilters] = useState({
        authorValue: '', journalType: '', keyword: '', qRanking: '',
        fromDate: initialFromDate, toDate: initialToDate
    });
    const [sortConfig, setSortConfig] = useState({ key: 'PublicationDate', direction: 'desc' });

    useEffect(() => {
        const fetchPublicData = async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('accessToken');
                const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

                const [articlesRes, usersRes] = await Promise.all([
                    fetch(`${process.env.REACT_APP_API_BASE_URL}/publicdashboard/articles`),
                    fetch(`${process.env.REACT_APP_API_BASE_URL}/user?mode=selection`, { headers }).catch(() => null)
                ]);

                if (articlesRes.ok) {
                    const allArticles = await articlesRes.json();
                    setData(allArticles.filter(a => ['Q1', 'Q2'].includes((a.QRanking || '').toUpperCase())));
                }
                if (usersRes && usersRes.ok) {
                    setUsers(await usersRes.json());
                }
            } catch (error) {
                console.error("Lỗi tải dữ liệu", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPublicData();
    }, []);

    const filterUserOptions = useMemo(() => {
        const map = new Map();

        users.forEach(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || '';
            const dept = u.DepartmentName || '';
            const rawSearchStr = `${fullName} ${email} ${dept}`;

            if (fullName) {
                map.set(fullName.toLowerCase(), {
                    label: fullName,
                    searchKey: superCleanString(rawSearchStr),
                    value: u.Id,
                    fullName: fullName,
                    email: email,
                    dept: dept
                });
            }
        });

        data.forEach(item => {
            const extractName = (name) => {
                name = (name || '').trim();
                if (name && name !== 'Ẩn danh' && !name.toLowerCase().includes('cộng sự')) {
                    const key = name.toLowerCase();
                    if (!map.has(key)) {
                        map.set(key, {
                            label: name,
                            searchKey: superCleanString(name),
                            value: name,
                            fullName: name,
                            email: '',
                            dept: 'Tác giả ngoài'
                        });
                    }
                }
            };

            extractName(item.FullName);
            try {
                const membersList = JSON.parse(item.MembersJSON || '[]');
                membersList.forEach(m => extractName(m.FullName || m.fullName));
            } catch (e) { }
        });

        return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'vi'));
    }, [users, data]);

    const journalTypes = useMemo(() => {
        const types = data.map(d => d.JournalType).filter(Boolean);
        return [...new Set(types)].sort();
    }, [data]);

    const searchAuthor = (event) => {
        const query = superCleanString(event.query);
        if (!query.trim().length) {
            setFilteredAuthors([...filterUserOptions]);
        } else {
            setFilteredAuthors(filterUserOptions.filter(a => a.searchKey.includes(query)));
        }
    };

    const userFilterTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                {(option.email || option.dept) && (
                    <span style={{ fontSize: '12px', color: '#666' }}>
                        {option.email} {option.email && option.dept ? ' - ' : ''} {option.dept}
                    </span>
                )}
            </div>
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const filteredData = useMemo(() => {
        let result = data.filter(item => {
            if (filters.journalType && item.JournalType !== filters.journalType) return false;
            if (filters.qRanking && item.QRanking !== filters.qRanking) return false;

            if (filters.authorValue) {
                const target = filters.authorValue.toString();
                let isMatch = false;

                if (/^\d+$/.test(target)) {
                    try {
                        const mList = JSON.parse(item.MembersJSON || '[]');
                        isMatch = mList.some(m => (m.UserId || m.userId || m.Id)?.toString() === target);
                    } catch (e) { }

                    if (!isMatch) {
                        const selectedUser = users.find(u => u.Id.toString() === target);
                        if (selectedUser) {
                            const targetName = superCleanString(`${selectedUser.LastName || ''} ${selectedUser.FirstName || ''}`);
                            isMatch = superCleanString(item.FullName).includes(targetName);
                        }
                    }
                } else {
                    const targetName = superCleanString(target);
                    isMatch = superCleanString(item.FullName).includes(targetName);
                    if (!isMatch) {
                        try {
                            const mList = JSON.parse(item.MembersJSON || '[]');
                            isMatch = mList.some(m => superCleanString(m.FullName || m.fullName).includes(targetName));
                        } catch (e) { }
                    }
                }

                if (!isMatch) return false;
            }

            if (filters.keyword) {
                const searchKeyword = superCleanString(filters.keyword);
                const title = superCleanString(item.Title);
                const journalName = superCleanString(item.JournalName);
                if (!title.includes(searchKeyword) && !journalName.includes(searchKeyword)) return false;
            }

            if (filters.fromDate || filters.toDate) {
                if (!item.PublicationDate) return false;
                const itemDate = new Date(item.PublicationDate);
                itemDate.setHours(0, 0, 0, 0);
                if (filters.fromDate && itemDate < new Date(filters.fromDate)) return false;
                if (filters.toDate && itemDate > new Date(filters.toDate)) return false;
            }
            return true;
        });

        if (sortConfig) {
            result.sort((a, b) => {
                if (sortConfig.key === 'PublicationDate') {
                    const dateA = a.PublicationDate ? new Date(a.PublicationDate).getTime() : 0;
                    const dateB = b.PublicationDate ? new Date(b.PublicationDate).getTime() : 0;
                    return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
                } else {
                    const valA = (a[sortConfig.key] || '').toString().trim();
                    const valB = (b[sortConfig.key] || '').toString().trim();
                    return sortConfig.direction === 'asc' ? valA.localeCompare(valB, 'vi') : valB.localeCompare(valA, 'vi');
                }
            });
        }
        return result;
    }, [data, filters, sortConfig, users]);

    const handleViewDetail = (item) => {
        let parsedMembers = [];
        try { parsedMembers = JSON.parse(item.MembersJSON || '[]'); } catch (e) { parsedMembers = []; }
        setFormData(item);
        setMembers(parsedMembers);
        setModalMode('view');
    };

    return (
        <div className="page-container" style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
            <Toast ref={toast} position="top-right" />
            <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ textTransform: 'uppercase', color: '#f59e0b', margin: 0 }}>Danh sách Bài báo phân hạng Q1/Q2</h2>
                <button className="btn-cancel" onClick={() => window.history.back()} style={{ margin: 0, backgroundColor: '#f59e0b', color: '#fff', border: 'none', padding: '8px 15px', borderRadius: '4px', cursor: 'pointer' }}>
                    <i className="fa-solid fa-arrow-left"></i> Trở về Dashboard
                </button>
            </div>

            <div className="theme-orange" style={{ backgroundColor: '#fffbeb', padding: '20px', borderRadius: '8px', marginBottom: '20px', borderTop: '4px solid #f59e0b' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' }}>

                    <div style={{ width: '100%' }}>
                        <AutoComplete
                            value={selectedAuthor}
                            suggestions={filteredAuthors}
                            completeMethod={searchAuthor}
                            field="label"
                            dropdown
                            placeholder="Họ tên giảng viên"
                            itemTemplate={userFilterTemplate}
                            onChange={(e) => {
                                const val = e.value;
                                if (typeof val === 'object' && val !== null) {
                                    setSelectedAuthor(val.label);
                                    setFilters(prev => ({ ...prev, authorValue: val.value }));
                                } else {
                                    setSelectedAuthor(val);
                                    if (!val || val.trim() === '') {
                                        setFilters(prev => ({ ...prev, authorValue: '' }));
                                    }
                                }
                            }}
                            style={{ width: '100%', height: '40px' }}
                            inputStyle={{ width: '100%', padding: '0 12px', border: '1px solid #ced4da', borderRadius: '4px', fontFamily: 'inherit' }}
                            panelStyle={{ width: 'auto', minWidth: '350px', maxWidth: '500px' }}
                            emptyMessage="Không tìm thấy giảng viên"
                        />
                    </div>

                    <select name="journalType" className="form-input" style={{ width: '100%', height: '40px' }} value={filters.journalType} onChange={handleFilterChange}>
                        <option value="">Tất cả loại tạp chí</option>
                        {journalTypes.map(level => <option key={level} value={level}>{level}</option>)}
                    </select>

                    <select name="qRanking" className="form-input" style={{ width: '100%', height: '40px' }} value={filters.qRanking} onChange={handleFilterChange}>
                        <option value="">Tất cả (Q1 & Q2)</option>
                        <option value="Q1">Q1</option>
                        <option value="Q2">Q2</option>
                    </select>

                    <Calendar value={filters.fromDate} onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.value }))} placeholder="Từ ngày công bố" dateFormat="dd/mm/yy" showIcon className="custom-prime-calendar" style={{ width: '100%', height: '40px' }} inputStyle={{ paddingLeft: '12px' }} />
                    <Calendar value={filters.toDate} onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.value }))} placeholder="Đến ngày công bố" dateFormat="dd/mm/yy" showIcon className="custom-prime-calendar" style={{ width: '100%', height: '40px' }} inputStyle={{ paddingLeft: '12px' }} />

                    <div className="search-wrapper" style={{ position: 'relative', width: '100%', gridColumn: '1 / -1' }}>
                        <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '10px', top: '12px', color: '#888' }}></i>
                        <input type="text" name="keyword" placeholder="Tiêu đề bài báo hoặc tạp chí" className="form-input" style={{ paddingLeft: '35px', width: '100%', height: '40px' }} value={filters.keyword} onChange={handleFilterChange} />
                    </div>
                </div>
            </div>

            <ScientificArticlesMngtListing
                data={filteredData}
                isPublicView={true}
                themeColor="#f59e0b"
                onViewDetail={handleViewDetail}
                isLoading={isLoading}
                sortConfig={sortConfig}
                requestSort={(key) => setSortConfig({ key, direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc' })}
            />

            <ScientificArticleMngtForm
                isOpen={modalMode === 'view'}
                onClose={() => setModalMode(null)}
                formData={formData}
                setFormData={setFormData}
                readOnly={true}
                members={members}
                setMembers={setMembers}
                users={[]}
                fields={[]}
                articleTypes={[]}
                countries={[]}
                journalTypes={[]}
            />
        </div>
    );
};

export default PublicQ1Q2Article;